import type { ColDef } from "ag-grid-community";
import { makeAutoObservable } from "mobx";
import { makeStoreProvider } from "@synthesis/shared-utils";

import { DocumentKitDocumentsCell } from "./local-components/document-kit-documents-cell";
import { DocumentKitExpandCell } from "./local-components/document-kit-expand-cell";

export type ConstructorDocumentKit = {
  id: string;
  service: string;
  road: string;
  connectionType: string;
  applicantType: string;
  workTypesLabel: string;
  documentsTotal: number;
  documentsRequired: number;
  /** Детализация комплекта — для раскрытой строки (первый комплект ППР развёрнут в макете). */
  expanded?: {
    documents: { name: string; description: string; required: boolean }[];
    workTypesNote: string;
  };
};

// Бэкенда нет — комплекты документов захардкожены по макету «Конструктор - Документы».
export const MOCK_CONSTRUCTOR_DOCUMENT_KITS: ConstructorDocumentKit[] = [
  {
    id: "tu-issue-okt",
    service: "Выдача ТУ",
    road: "Октябрьская",
    connectionType: "Примыкание",
    applicantType: "Юридическое лицо",
    workTypesLabel: "2 вида",
    documentsTotal: 17,
    documentsRequired: 16,
  },
  {
    id: "tu-issue-any-crossing",
    service: "Выдача ТУ",
    road: "Любая",
    connectionType: "Пересечение",
    applicantType: "Любой",
    workTypesLabel: "—",
    documentsTotal: 13,
    documentsRequired: 12,
  },
  {
    id: "akt-dopuska",
    service: "Акт-допуска",
    road: "Любая",
    connectionType: "Любой",
    applicantType: "Юридическое лицо",
    workTypesLabel: "2 вида",
    documentsTotal: 4,
    documentsRequired: 3,
  },
  {
    id: "ppr",
    service: "ППР",
    road: "Любая",
    connectionType: "Любой",
    applicantType: "Любой",
    workTypesLabel: "1 вид",
    documentsTotal: 35,
    documentsRequired: 35,
    expanded: {
      documents: [
        { name: "Географические координаты", description: "Координаты предполагаемого места размещения объекта", required: true },
        { name: "Границы полос отвода объектов", description: "Сведения о границах полос отвода затрагиваемых объектов", required: true },
        { name: "Мероприятия по охране труда и безопасности", description: "Охрана труда и безопасность при проведении работ", required: true },
        { name: "Иные материалы", description: "Дополнительные материалы по усмотрению заявителя, не вошедшие в основной перечень", required: false },
      ],
      workTypesNote: "Виды работ: Проведение работ по установке и демонтажу страховочных пакетов, Проведение работ методом ГНБ или другим методом.",
    },
  },
  {
    id: "ppr-cross",
    service: "ППР",
    road: "Любая",
    connectionType: "Любой",
    applicantType: "Любой",
    workTypesLabel: "3 вида",
    documentsTotal: 4,
    documentsRequired: 2,
  },
  {
    id: "doc-approve",
    service: "Согласование документации",
    road: "Любая",
    connectionType: "Любой",
    applicantType: "Юридическое лицо",
    workTypesLabel: "—",
    documentsTotal: 4,
    documentsRequired: 2,
  },
];

export const DOCUMENT_KIT_DOCUMENTS = [
  { id: "constituent", name: "Заверенные копии учредительных документов", required: true },
  { id: "tax-registration", name: "Свидетельство / документ о постановке на учёт в налоговом органе", required: true },
  { id: "egrul", name: "Документ, подтверждающий внесение записи в ЕГРЮЛ", required: true },
  { id: "sp-registration", name: "Заверенная копия свидетельства о государственной регистрации ИП", required: true },
  { id: "land-rights", name: "Правоустанавливающие документы на земельные участки", required: true },
  { id: "topographic", name: "Топографический план местности", required: true },
  { id: "cadastral", name: "Кадастровые номера земельных участков", required: true },
  { id: "station-plan", name: "Схематический план станции с местом примыкания", required: true },
  { id: "owner-consent", name: "Письменное согласие владельца пути необщего пользования", required: true },
  { id: "track-plan", name: "Схематический план существующего путевого развития", required: false },
  { id: "cargo-volume", name: "Информация об объёмах перевозок", required: false },
  { id: "correspondence", name: "Сведения по каждой корреспонденции", required: false },
] as const;

// ---------------------------------- COLUMNS ----------------------------------

export const DOCUMENT_KIT_COLUMNS: ColDef<ConstructorDocumentKit>[] = [
  { colId: "expand", headerName: "", width: 48, cellRenderer: DocumentKitExpandCell, sortable: false },
  { colId: "service", headerName: "Услуга", field: "service", width: 240 },
  { colId: "road", headerName: "Дорога", field: "road", width: 140 },
  { colId: "connection", headerName: "Тип присоединения", field: "connectionType", width: 200 },
  { colId: "applicant", headerName: "Заявитель", field: "applicantType", width: 200 },
  { colId: "workTypes", headerName: "Виды работ", field: "workTypesLabel", width: 140 },
  { colId: "documents", headerName: "Документов", width: 220, cellRenderer: DocumentKitDocumentsCell },
];

// ---------------------------------- STORE ----------------------------------

export type DocumentKitModalAction = { type: "add-document-kit" };

class PageStore {
  public popupAction: DocumentKitModalAction | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public setPopupAction(action: DocumentKitModalAction | null) {
    this.popupAction = action;
  }
}

const { StoreProvider: PageStoreProvider, useStore: usePageStore } = makeStoreProvider(PageStore);

export { PageStoreProvider, usePageStore };
